import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SubmissionStepType } from "generated-graphql/graphql";
import { stepperStepPath } from "hooks/useStepperNavigation";
import { SubmissionStepState } from "hooks/useSubmissionSteps";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { FrozenStepContent } from "./FrozenStepPanel";
import { FrozenSubmissionBanner } from "./FrozenSubmissionBanner";
import { StepHeader } from "./StepHeader";
import { SNAPSHOT_FROZEN_STEPS } from "./StepRoute";
import { SubmissionStepStatusIcon } from "./SubmissionStepStatusIcon";

/**
 * A single read-only Overview section. Defaults collapsed and lazy-mounts its
 * content only once expanded — so opening the Overview doesn't fire every
 * section's queries (and the heavy waste-streams grid) at once. Frozen
 * snapshot-backed steps render the filed snapshot body; everything else renders
 * the live editor, which already renders read-only when the user can't edit.
 */
function ReadOnlySection({
  step,
  status,
  submissionId,
  facilityId,
  isFrozen,
  expanded,
  onToggle,
}: {
  step: SubmissionStepType;
  status?: SubmissionStepState;
  submissionId: string;
  facilityId: string;
  isFrozen: boolean;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
}) {
  const theme = useTheme();
  const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];

  const renderContent = () => {
    if (isFrozen && SNAPSHOT_FROZEN_STEPS.has(step)) {
      return <FrozenStepContent step={step} submissionId={submissionId} />;
    }
    // Only OVERVIEW lacks an `Editor`, and it's filtered out of `steps` below;
    // the guard keeps the type honest (mirrors `StepRoute`).
    const { Editor } = presentation;
    if (!Editor) {
      return null;
    }
    return <Editor submissionId={submissionId} facilityId={facilityId} />;
  };

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" gap={theme.spacing(1.5)}>
          <SubmissionStepStatusIcon status={status} />
          <Typography>{presentation.label}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{expanded ? renderContent() : null}</AccordionDetails>
    </Accordion>
  );
}

/**
 * Stepper landing page.
 *
 * For staff editing the submission, it's a clean index of the flow: each
 * applicable step (from the backend-ordered `stepperSteps`) with its status icon
 * and a link to its editor route.
 *
 * For read-only users — reviewers and filed (frozen) submissions — it instead
 * shows every section's content in collapsible accordions, so the whole record
 * can be reviewed in one place without clicking into each step. Sections default
 * collapsed and lazy-mount on expand. Frozen submissions also show the read-only
 * lifecycle banner on top.
 */
export function StepperOverview() {
  const { tenantId = "", submissionId = "" } = useParams<{
    tenantId: string;
    submissionId: string;
  }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { submission, facilityId, stepStates, isFrozen, canEdit } = useWasteStepper();
  const readOnly = !canEdit;

  // The Overview step is the page itself; don't list it as a navigable row.
  const steps = (submission?.stepperSteps ?? []).filter(
    ({ step }) => WASTE_SUBMISSION_STEP_PRESENTATION[step].routeSlug !== ""
  );

  const [expandedSteps, setExpandedSteps] = useState<Set<SubmissionStepType>>(new Set());
  const allExpanded = steps.length > 0 && steps.every(({ step }) => expandedSteps.has(step));
  const toggleAll = () =>
    setExpandedSteps(allExpanded ? new Set() : new Set(steps.map(({ step }) => step)));
  const toggleStep = (step: SubmissionStepType, expanded: boolean) =>
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(step);
      } else {
        next.delete(step);
      }
      return next;
    });

  return (
    <Stack spacing={2}>
      {isFrozen && <FrozenSubmissionBanner />}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <StepHeader
          title="Submission Overview"
          description={
            readOnly
              ? "Expand any section to review its details. Use the sidebar to open an individual section."
              : "Review and complete each section before submitting this 8700-12 notification."
          }
        />
        {readOnly && steps.length > 0 && (
          <Button size="small" onClick={toggleAll} sx={{ flexShrink: 0, mt: 0.5 }}>
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>

      {readOnly ? (
        <Stack spacing={2}>
          {steps.map(({ step }) => (
            <ReadOnlySection
              key={step}
              step={step}
              status={stepStates[step]}
              submissionId={submissionId}
              facilityId={facilityId}
              isFrozen={isFrozen}
              expanded={expandedSteps.has(step)}
              onToggle={(expanded) => toggleStep(step, expanded)}
            />
          ))}
        </Stack>
      ) : (
        <List>
          {steps.map(({ step }) => {
            const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];
            return (
              <ListItem key={step} disablePadding>
                <ListItemButton
                  onClick={() =>
                    navigate(stepperStepPath(tenantId, submissionId, presentation.routeSlug))
                  }
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: theme.spacing(2) }}>
                    <SubmissionStepStatusIcon status={stepStates[step]} />
                  </ListItemIcon>
                  <ListItemText
                    primary={presentation.label}
                    secondary={presentation.sourceDescription}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Stack>
  );
}
